/**
 * <CmsBackground> — image de fond absolute pour un hero, avec overlay coloré,
 * flou et position fixe (parallax) optionnels. Lit toutes les sous-keys du CMS.
 *
 * Usage :
 *   <section className="relative">
 *     <CmsBackground cmsKey="home.hero.b2c.bg" />
 *     <div className="relative z-10">... contenu ...</div>
 *   </section>
 *
 * Sous-keys reconnues (toutes optionnelles) :
 *   <key>                  URL de l'image
 *   <key>.opacity          0–100
 *   <key>.blur             0–20 (px)
 *   <key>.overlay_color    hex (#RRGGBB)
 *   <key>.overlay_opacity  0–100
 *   <key>.fixed            "true" → effet parallax (background-attachment: fixed)
 *
 * Si aucune image n'est définie, ne rend RIEN.
 */
import { useSiteContent } from "@/lib/useSiteContent";

export default function CmsBackground({ cmsKey, className = "" }) {
    const keys = [
        cmsKey,
        `${cmsKey}.opacity`,
        `${cmsKey}.blur`,
        `${cmsKey}.overlay_color`,
        `${cmsKey}.overlay_opacity`,
        `${cmsKey}.fixed`,
    ];
    const c = useSiteContent(keys);

    const src = c(cmsKey, "");
    if (!src) return null;

    const opacity = parseFloat(c(`${cmsKey}.opacity`, "100"));
    const blur = parseFloat(c(`${cmsKey}.blur`, "0"));
    const overlayColor = c(`${cmsKey}.overlay_color`, "#0A3B2A");
    const overlayOpacity = parseFloat(c(`${cmsKey}.overlay_opacity`, "0"));
    // "fixed" peut être stocké "true"/"false" ou booléen — on accepte les deux
    const fixedRaw = c(`${cmsKey}.fixed`, "");
    const fixed = fixedRaw === true || fixedRaw === "true" || fixedRaw === "1";

    const opacityVal = (Number.isFinite(opacity) ? opacity : 100) / 100;
    const filterVal = blur > 0 ? `blur(${blur}px)` : undefined;

    return (
        <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} aria-hidden="true">
            {fixed ? (
                // Mode "fond fixe" (effet parallax) : <div> avec background-image
                // pour bénéficier de `background-attachment: fixed` (impossible
                // sur un <img>).
                <div
                    className="absolute inset-0 w-full h-full"
                    style={{
                        backgroundImage: `url("${src}")`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        backgroundAttachment: "fixed",
                        opacity: opacityVal,
                        filter: filterVal,
                    }}
                />
            ) : (
                <img
                    src={src}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ opacity: opacityVal, filter: filterVal }}
                />
            )}
            {overlayOpacity > 0 && (
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundColor: overlayColor,
                        opacity: overlayOpacity / 100,
                    }}
                />
            )}
        </div>
    );
}
